<template>
  <div id="app">
    <AppNavigation />
    <!-- Aquí se inyectará el contenido de cada ruta -->
    <router-view />
    <AppFooter />
  </div>
</template>

<script>
import { markRaw } from 'vue';
import AppNavigation from './components/AppNavigation.vue';
import AppFooter from './components/AppFooter.vue';

let scrollTriggerRegistered = false;

export default {
  name: 'App',
  data() {
    return {
      motionMedia: null,
      motionGeneration: 0,
      refreshFrame: null,
      removeRouteHook: null
    };
  },
  components: {
    AppNavigation,
    AppFooter
  },
  mounted() {
    this.$nextTick(this.setupScrollMotion);
    this.removeRouteHook = this.$router.afterEach((to, from) => {
      if (to.path === from.path) {
        return;
      }

      this.$nextTick(this.setupScrollMotion);
    });
  },
  beforeUnmount() {
    this.disposeScrollMotion();
    this.removeRouteHook?.();
  },
  methods: {
    async setupScrollMotion() {
      this.disposeScrollMotion();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const generation = this.motionGeneration;
      let gsap;
      let ScrollTrigger;

      try {
        const [gsapModule, scrollTriggerModule] = await Promise.all([
          import('gsap'),
          import('gsap/ScrollTrigger')
        ]);
        gsap = gsapModule.gsap;
        ScrollTrigger = scrollTriggerModule.ScrollTrigger;
      } catch (error) {
        console.error('Unable to load scroll animations.', error);
        return;
      }

      if (generation !== this.motionGeneration || !this.$el) {
        return;
      }

      if (!scrollTriggerRegistered) {
        gsap.registerPlugin(ScrollTrigger);
        scrollTriggerRegistered = true;
      }

      const revealTargets = Array.from(this.$el.querySelectorAll('.reveal-up'))
        .filter((element) => {
          const documentTop = window.scrollY + element.getBoundingClientRect().top;
          return documentTop > window.innerHeight * 0.9;
        });
      const progressBar = this.$el.querySelector('.scroll-progress-bar');
      const motionMedia = gsap.matchMedia();

      motionMedia.add(
        {
          reduceMotion: '(prefers-reduced-motion: reduce)',
          allowMotion: '(prefers-reduced-motion: no-preference)'
        },
        ({ conditions }) => {
          if (conditions.reduceMotion) {
            return;
          }

          if (progressBar) {
            gsap.fromTo(
              progressBar,
              { scaleX: 0 },
              {
                scaleX: 1,
                ease: 'none',
                scrollTrigger: {
                  trigger: document.documentElement,
                  start: 'top top',
                  end: 'max',
                  scrub: true
                }
              }
            );
          }

          revealTargets.forEach((element) => {
            gsap.fromTo(
              element,
              { autoAlpha: 0, y: 12 },
              {
                autoAlpha: 1,
                y: 0,
                duration: 0.55,
                ease: 'power2.out',
                clearProps: 'opacity,visibility,transform',
                scrollTrigger: {
                  trigger: element,
                  start: 'top 88%',
                  once: true
                }
              }
            );
          });
        },
        this.$el
      );

      this.motionMedia = markRaw(motionMedia);
      this.refreshFrame = window.requestAnimationFrame(() => {
        ScrollTrigger.refresh();
        this.refreshFrame = null;
      });
    },
    disposeScrollMotion() {
      this.motionGeneration += 1;

      if (this.refreshFrame !== null) {
        window.cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = null;
      }

      this.motionMedia?.revert();
      this.motionMedia = null;
    }
  }
};
</script>
